import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTheme } from '@mui/material'
import DrawIcon from '@mui/icons-material/Draw'
import AbcIcon from '@mui/icons-material/Abc'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { backendClient } from '../../../api/backend'

const GOLD = '#c9a96e'

// ─── OTP Input component (6 individual boxes) ─────────────────────────────────
const OTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([])

  const handleChange = (index, val) => {
    if (!/^[0-9]?$/.test(val)) return
    const next = [...value]
    next[index] = val
    onChange(next)
    if (val && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length) {
      const next = pasted.split('').concat(Array(6).fill('')).slice(0, 6)
      onChange(next)
      inputRefs.current[Math.min(pasted.length, 5)]?.focus()
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
      {value.map((digit, i) => (
        <TextField
          key={i}
          inputRef={(el) => (inputRefs.current[i] = el)}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 700,
              padding: '10px 0',
              fontFamily: 'monospace',
            },
          }}
          sx={(theme) => ({
            width: 52,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              color: theme.palette.text.primary,
              '& fieldset': {
                borderColor: digit ? GOLD : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'),
                borderWidth: 2,
              },
              '&:hover fieldset': {
                borderColor: digit ? GOLD : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'),
              },
              '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 2 },
            },
          })}
        />
      ))}
    </Box>
  )
}

// ─── SigningDialog ──────────────────────────────────────────────────────────────
const SigningDialog = ({ open, onClose, contractId, contractTitle, onSigningComplete }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const user = useSelector((state) => state.auth?.user?.user)

  // Steps: sign | otp | done
  const [step, setStep] = useState('sign')
  const [error, setError] = useState(null)

  // Sign step
  const [signTab, setSignTab] = useState(0) // 0=draw, 1=type
  const [typedName, setTypedName] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [canvasHasContent, setCanvasHasContent] = useState(false)
  const canvasRef = useRef(null)
  const lastPos = useRef(null)

  // Saved signature — captured before canvas unmounts when moving to OTP step
  const savedSignature = useRef({ data: '', type: '' })

  // OTP step
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [otpLoading, setOtpLoading] = useState(false)
  const [otpError, setOtpError] = useState(null)
  const [countdown, setCountdown] = useState(0)
  const [submitting, setSubmitting] = useState(false)

  // ── Reset all state when dialog opens or closes ──────────────────────────────
  useEffect(() => {
    if (open) {
      setStep('sign')
      setError(null)
      setSignTab(0)
      setTypedName('')
      setAgreed(false)
      setIsDrawing(false)
      setCanvasHasContent(false)
      setOtp(['', '', '', '', '', ''])
      setOtpLoading(false)
      setOtpError(null)
      setCountdown(0)
      setSubmitting(false)
      savedSignature.current = { data: '', type: '' }
      lastPos.current = null
    }
  }, [open])

  // ── Canvas setup ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || step !== 'sign' || signTab !== 0) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.strokeStyle = '#1c1917' // Always dark ink on white canvas
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [open, step, signTab])

  const getCanvasPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    // Scale from CSS size to canvas internal resolution
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    }
  }

  const handleCanvasDown = useCallback((e) => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    setIsDrawing(true)
    const pos = getCanvasPos(e, canvas)
    lastPos.current = pos
    const ctx = canvas.getContext('2d')
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }, [])

  const handleCanvasMove = useCallback((e) => {
    e.preventDefault()
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const pos = getCanvasPos(e, canvas)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    lastPos.current = pos
    setCanvasHasContent(true)
  }, [isDrawing])

  const handleCanvasUp = useCallback(() => setIsDrawing(false), [])

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
    setCanvasHasContent(false)
  }

  // ── Countdown timer ─────────────────────────────────────────────────────────
  const countdownRef = useRef(null)

  const startCountdown = (seconds) => {
    if (countdownRef.current) clearInterval(countdownRef.current)
    setCountdown(seconds)
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current)
          countdownRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current)
    }
  }, [])

  // ── Send OTP ────────────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    setOtpLoading(true)
    setOtpError(null)
    setError(null)
    try {
      await backendClient.sendInternalOTP(contractId)
      setStep('otp')
      setOtp(['', '', '', '', '', ''])
      startCountdown(60)
    } catch (err) {
      setError(err.message || 'Failed to send OTP')
    } finally {
      setOtpLoading(false)
    }
  }

  // ── On "Continuer" click: capture signature then send OTP ───────────────────
  const handleContinueToOtp = () => {
    if (!agreed) return
    if (signTab === 0 && !canvasHasContent) return
    if (signTab === 1 && !typedName.trim()) return

    // Capture signature data NOW while canvas is still mounted
    if (signTab === 0) {
      const canvas = canvasRef.current
      savedSignature.current = {
        data: canvas ? canvas.toDataURL('image/png').split(',')[1] : '',
        type: 'drawn',
      }
    } else {
      savedSignature.current = { data: typedName.trim(), type: 'typed' }
    }

    handleSendOtp()
  }

  // ── Auto-submit when all 6 digits entered ───────────────────────────────────
  useEffect(() => {
    if (step !== 'otp') return
    const otpValue = otp.join('')
    if (otpValue.length === 6) {
      handleVerifyAndSubmit(otpValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, step])

  // ── Verify OTP then submit signature ────────────────────────────────────────
  const handleVerifyAndSubmit = async (otpValue) => {
    setSubmitting(true)
    setOtpError(null)
    try {
      // Step 1: verify OTP
      await backendClient.verifyInternalOTP(contractId, otpValue)

      // Step 2: use signature data saved before canvas unmounted
      const { data: signatureData, type: signatureType } = savedSignature.current

      // Step 3: submit signature
      await backendClient.submitInternalSignature(contractId, signatureData, signatureType)

      // Step 4: notify parent
      if (onSigningComplete) onSigningComplete()

      // Step 5: show done step
      setStep('done')
    } catch (err) {
      setOtpError(err.message || 'Verification failed')
      setOtp(['', '', '', '', '', ''])
    } finally {
      setSubmitting(false)
    }
  }

  const canContinue = agreed && (signTab === 0 ? canvasHasContent : typedName.trim().length > 0)

  const isDark = theme.palette.mode === 'dark'
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  // ── Handle close (prevent closing during submission) ────────────────────────
  const handleClose = () => {
    if (submitting || otpLoading) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDark
            ? '0 4px 20px rgba(0,0,0,0.5)'
            : '0 4px 20px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 3,
          pb: step === 'done' ? 0 : 1,
        }}
      >
        {step !== 'done' && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {t('signing.dialogTitle')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
              {t('signing.dialogSubtitle')}
            </Typography>
          </Box>
        )}
        {step === 'done' && <Box />}
        <IconButton
          onClick={handleClose}
          disabled={submitting || otpLoading}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, pb: 3, pt: step === 'done' ? 0 : 2 }}>

        {/* ── STEP 1: SIGN ── */}
        {step === 'sign' && (
          <Box>
            {/* Signature tabs */}
            <Tabs
              value={signTab}
              onChange={(_, v) => {
                setSignTab(v)
                clearCanvas()
                setTypedName('')
              }}
              sx={{
                mb: 2,
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  minWidth: 0,
                },
                '& .MuiTabs-indicator': { backgroundColor: GOLD },
                '& .Mui-selected': { color: `${GOLD} !important` },
              }}
            >
              <Tab
                icon={<DrawIcon sx={{ fontSize: 16 }} />}
                iconPosition="start"
                label={t('signing.tabDraw')}
              />
              <Tab
                icon={<AbcIcon sx={{ fontSize: 16 }} />}
                iconPosition="start"
                label={t('signing.tabType')}
              />
            </Tabs>

            {/* Draw tab */}
            {signTab === 0 && (
              <Box>
                <Box
                  sx={{
                    border: `2px dashed ${canvasHasContent ? GOLD : borderColor}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: '#ffffff',
                    transition: 'border-color 0.2s',
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    width={640}
                    height={200}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 200,
                      cursor: 'crosshair',
                      touchAction: 'none',
                    }}
                    onMouseDown={handleCanvasDown}
                    onMouseMove={handleCanvasMove}
                    onMouseUp={handleCanvasUp}
                    onMouseLeave={handleCanvasUp}
                    onTouchStart={handleCanvasDown}
                    onTouchMove={handleCanvasMove}
                    onTouchEnd={handleCanvasUp}
                  />
                  {!canvasHasContent && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography sx={{ color: 'rgba(0,0,0,0.35)', fontSize: '0.85rem' }}>
                        {t('signing.drawPlaceholder')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Type tab */}
            {signTab === 1 && (
              <Box>
                <TextField
                  fullWidth
                  placeholder={t('signing.typePlaceholder')}
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  inputProps={{
                    style: {
                      fontFamily: 'Georgia, serif',
                      fontSize: '1.1rem',
                      fontStyle: 'italic',
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: typedName ? GOLD : undefined },
                    },
                  }}
                />
                {typedName && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 2,
                      border: `1px solid ${borderColor}`,
                      borderRadius: '8px',
                      backgroundColor: isDark ? alpha('#fff', 0.02) : '#fafafa',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Georgia, serif',
                        fontSize: '1.4rem',
                        fontStyle: 'italic',
                        color: 'text.primary',
                        borderBottom: `1px solid ${alpha(GOLD, 0.5)}`,
                        pb: 0.5,
                        display: 'inline-block',
                      }}
                    >
                      {typedName}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Agreement checkbox + Clear button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    sx={{
                      color: GOLD,
                      '&.Mui-checked': { color: GOLD },
                      py: 0,
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    {t('signing.agreeCheckbox')}
                  </Typography>
                }
                sx={{ alignItems: 'center', mr: 0 }}
              />
              {canvasHasContent && signTab === 0 && (
                <Button
                  size="small"
                  startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                  onClick={clearCanvas}
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    flexShrink: 0,
                  }}
                >
                  {t('signing.clearButton')}
                </Button>
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              disabled={!canContinue || otpLoading}
              onClick={handleContinueToOtp}
              sx={{
                mt: 2.5,
                height: 48,
                fontWeight: 700,
                fontSize: '0.95rem',
                borderRadius: '10px',
                backgroundColor: canContinue ? GOLD : undefined,
                color: canContinue ? '#1c1917' : undefined,
                boxShadow: 'none',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: canContinue ? alpha(GOLD, 0.88) : undefined,
                  boxShadow: 'none',
                },
                '&:disabled': { opacity: 0.45 },
              }}
            >
              {otpLoading ? (
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
              ) : (
                t('signing.continueButton')
              )}
            </Button>
          </Box>
        )}

        {/* ── STEP 2: OTP ── */}
        {step === 'otp' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('signing.otpTitle')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 0.5 }}>
              {t('signing.otpSentTo', { email: user?.email || '' })}
            </Typography>
            <Typography sx={{ fontWeight: 600, color: GOLD, mb: 3 }}>
              {user?.email}
            </Typography>

            <OTPInput value={otp} onChange={setOtp} disabled={submitting} />

            {otpError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '8px', textAlign: 'left' }}>
                {otpError}
              </Alert>
            )}

            {submitting && (
              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={18} sx={{ color: GOLD }} />
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {t('signing.otpVerifying')}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              {countdown > 0 ? (
                <Typography sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
                  {t('signing.otpResendCountdown', { seconds: countdown })}
                </Typography>
              ) : (
                <Button
                  variant="text"
                  onClick={handleSendOtp}
                  disabled={otpLoading}
                  sx={{
                    textTransform: 'none',
                    color: GOLD,
                    fontSize: '0.82rem',
                  }}
                >
                  {otpLoading ? (
                    <CircularProgress size={14} sx={{ color: GOLD }} />
                  ) : (
                    t('signing.otpResend')
                  )}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* ── STEP 3: DONE ── */}
        {step === 'done' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('signing.doneTitle')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 3 }}>
              {t('signing.doneMessage')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                height: 44,
                fontWeight: 700,
                fontSize: '0.9rem',
                borderRadius: '10px',
                backgroundColor: GOLD,
                color: '#1c1917',
                boxShadow: 'none',
                textTransform: 'none',
                px: 4,
                '&:hover': {
                  backgroundColor: alpha(GOLD, 0.88),
                  boxShadow: 'none',
                },
              }}
            >
              {t('signing.continueButton')}
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

export default SigningDialog
